import type {
  ArticleActivityPeriod,
  ArticleSuggestion,
} from "../api/articles";
import {
  activityPeriodHint,
  articleMatchKindLabel,
  formatArticleQtyYtd,
  formatInvoiceActivity12m,
} from "../lib/articleActivity";
import { cleanDescription } from "../lib/description";
import { formatDateShort, formatEuro } from "../lib/format";

export default function ArticleSuggestionContent({
  article,
  activityPeriod,
}: {
  article: ArticleSuggestion;
  activityPeriod?: ArticleActivityPeriod | null;
}) {
  const matchLabel = articleMatchKindLabel(article.match_kind);
  const description = article.description_example
    ? cleanDescription(article.description_example)
    : null;
  const metaLine = [article.product_family, article.brand_name]
    .map((value) => value?.trim())
    .filter((value): value is string => Boolean(value))
    .join(" · ");
  const activity12mTitle = `${activityPeriodHint(
    activityPeriod,
    "rolling12m",
  )}${
    article.last_invoice_doc_date_12m
      ? ` Letzter Rechnungseinkauf: ${formatDateShort(
          article.last_invoice_doc_date_12m,
        )}.`
      : ""
  }`;

  return (
    <>
      <div className="flex min-w-0 items-start justify-between gap-3">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
          <span className="min-w-0 truncate font-mono text-slate-900">
            {article.primary_article_code ?? article.article_group_key}
          </span>
          {matchLabel ? (
            <span className="shrink-0 rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-800">
              {matchLabel}
            </span>
          ) : null}
        </div>
        <span
          className="shrink-0 text-xs text-slate-500"
          title="Letzter belegter Netto-Einheitspreis"
        >
          {formatEuro(article.last_net_unit_price)}
        </span>
      </div>
      <div className="mt-0.5 flex min-w-0 items-baseline justify-between gap-3 text-xs text-slate-500">
        <span className="truncate">
          {metaLine || "–"}
        </span>
        <span
          className="shrink-0 font-medium text-slate-600"
          title={activity12mTitle}
        >
          {formatInvoiceActivity12m(article.invoice_document_count_12m)}
        </span>
      </div>
      <div className="mt-0.5 flex min-w-0 items-baseline justify-between gap-3 text-xs text-slate-600">
        <span className="truncate" title={article.description_example ?? undefined}>
          {description ?? "Keine Beschreibung"}
        </span>
        <span
          className="shrink-0 text-slate-500"
          title={activityPeriodHint(activityPeriod, "ytd")}
        >
          Menge YTD: {formatArticleQtyYtd(article)}
        </span>
      </div>
    </>
  );
}
